import {FormEvent, useEffect, useState} from "react";
import {Modifier} from "../../../types/item";
import {DropdownOption} from "../type";
import {createApolloClient} from "../../../providers/ApolloClientFactory";
import {GET_ITEM_MODIFIER_BY_ORGANIZATION} from "../../item-modifiers/queries";
import CustomSelect from "../../../components/customSelect";
import IndeterminateCheckbox from "../../../components/inputs/IndeterminantCheckbox";
import {usdFormat} from "../../../utils";
import {PrimaryButton} from "../../../components/Button";
import {Bars3Icon} from "@heroicons/react/24/solid";

const client = createApolloClient();

interface ModifierSelectorProps {
    organizationId: number | null;
    initialModifierType?: string;
    initialModifiers?: Modifier[];
    onValidationChange?: (isValid: boolean) => void;
}

export default function ModifierSelector({
    organizationId,
    initialModifierType,
    initialModifiers,
    onValidationChange,
}: ModifierSelectorProps) {
    const [allItemModifiers, setAllItemModifiers] = useState<Modifier[]>([]);
    const [modifierTypes, setModifierTypes] = useState<DropdownOption[]>([]);
    const [selectedModifierType, setSelectedModifierType] = useState(
        initialModifierType || ""
    );

    const initialSubTypes = initialModifiers
        ? initialModifiers.reduce(
              (acc: {subType: string; multiSelection: boolean}[], modifier) => {
                  const existing = acc.find(
                      (s) => s.subType === modifier.sub_type
                  );
                  if (!existing) {
                      acc.push({
                          subType: modifier.sub_type,
                          multiSelection: modifier.multi_selection,
                      });
                  }
                  return acc;
              },
              []
          )
        : [{subType: "", multiSelection: false}];

    const [subTypes, setSubTypes] = useState(initialSubTypes);
    const [selectedItemModifiers, setSelectedItemModifiers] = useState<Modifier[]>(
        initialModifiers || []
    );

    const initialCheckedModifiers = (initialModifiers || []).reduce(
        (acc: Record<string, boolean>, modifier) => {
            acc[modifier.id] = true;
            return acc;
        },
        {}
    );
    const [checkedModifiers, setCheckedModifiers] = useState(initialCheckedModifiers);

    const initialDefaultModifiers = (initialModifiers || []).reduce(
        (acc: Record<string, boolean>, modifier) => {
            if (modifier.default) {
                acc[modifier.id] = true;
            }
            return acc;
        },
        {}
    );
    const [defaultModifiers, setDefaultModifiers] = useState(initialDefaultModifiers);

    // Drag-to-reorder state
    const [dragIndex, setDragIndex] = useState<number | null>(null);
    const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

    // Fetch all modifiers for the organization
    useEffect(() => {
        const fetchAllModifiers = async () => {
            const {data} = await client.query({
                query: GET_ITEM_MODIFIER_BY_ORGANIZATION,
                variables: {
                    where: organizationId
                        ? {organization_id: {_eq: organizationId}}
                        : {},
                },
            });
            const itemModifiers: Modifier[] = data.item_modifiers;
            setAllItemModifiers(itemModifiers);
            const types: DropdownOption[] = [
                ...new Set(itemModifiers.map((item) => item.type)),
            ].map((type) => ({
                label: type.charAt(0).toUpperCase() + type.slice(1),
                value: type,
            }));
            setModifierTypes(types);
        };
        fetchAllModifiers();
    }, [organizationId]);

    const handleCheckboxChange = (
        modifier: Modifier,
        isChecked: boolean | FormEvent<HTMLInputElement>
    ) => {
        const currentSubTypeIndex = subTypes.findIndex(
            (subType) => subType.subType === modifier.sub_type
        );
        const multiSelectionValue =
            subTypes[currentSubTypeIndex]?.multiSelection || false;

        setCheckedModifiers((prevState) => ({
            ...prevState,
            [modifier.id]: isChecked as boolean,
        }));

        if (isChecked) {
            setSelectedItemModifiers((prev) => [
                ...prev,
                {
                    id: modifier.id,
                    name: modifier.name,
                    sub_type: modifier.sub_type,
                    additional_price: modifier.additional_price,
                    multi_selection: multiSelectionValue,
                    default: defaultModifiers[modifier.id] || false,
                    type: modifier.type,
                },
            ]);
        } else {
            setSelectedItemModifiers((prev) =>
                prev.filter((item) => item.id !== modifier.id)
            );
        }
    };

    const handleMultiSelectionChange = (index: number, isChecked: boolean) => {
        setSubTypes((prev) => {
            const newSubTypes = [...prev];
            newSubTypes[index].multiSelection = isChecked;
            return newSubTypes;
        });

        setSelectedItemModifiers((prevModifiers) =>
            prevModifiers.map((modifier) =>
                modifier.sub_type === subTypes[index].subType
                    ? {...modifier, multi_selection: isChecked}
                    : modifier
            )
        );

        if (!isChecked) {
            setSelectedItemModifiers((prevModifiers) =>
                prevModifiers.map((modifier) =>
                    modifier.sub_type === subTypes[index].subType
                        ? {...modifier, default: false}
                        : modifier
                )
            );
        }
    };

    const handleDefaultChange = (modifierId: number, isDefault: boolean) => {
        setDefaultModifiers((prev) => ({
            ...prev,
            [modifierId]: isDefault,
        }));

        setSelectedItemModifiers((prevModifiers) =>
            prevModifiers.map((modifier) =>
                modifier.id === modifierId
                    ? {...modifier, default: isDefault}
                    : modifier
            )
        );
    };

    const handleAddSubType = () => {
        setSubTypes((prev) => [...prev, {subType: "", multiSelection: false}]);
    };

    const handleRemoveSubType = (index: number) => {
        const removedSubType = subTypes[index].subType;
        setSubTypes((prev) => prev.filter((_, i) => i !== index));

        setSelectedItemModifiers((prevModifiers) =>
            prevModifiers.filter(
                (modifier) => modifier.sub_type !== removedSubType
            )
        );

        // Clean up checked and default state for modifiers in the removed sub-type
        const modifierIds = allItemModifiers
            .filter((m) => m.sub_type === removedSubType)
            .map((m) => String(m.id));
        setCheckedModifiers((prev) => {
            const next = {...prev};
            modifierIds.forEach((id) => delete next[id]);
            return next;
        });
        setDefaultModifiers((prev) => {
            const next = {...prev};
            modifierIds.forEach((id) => delete next[id]);
            return next;
        });
    };

    const updateSubType = (index: number, value: string) => {
        setSubTypes((prev) => {
            const newSubTypes = [...prev];
            const oldSubType = newSubTypes[index].subType;

            if (oldSubType && oldSubType !== value) {
                setSelectedItemModifiers((prevModifiers) =>
                    prevModifiers.filter(
                        (modifier) => modifier.sub_type !== oldSubType
                    )
                );

                // Clean up checked and default state for modifiers in the old sub-type
                const modifierIds = allItemModifiers
                    .filter((m) => m.sub_type === oldSubType)
                    .map((m) => String(m.id));
                setCheckedModifiers((prev) => {
                    const next = {...prev};
                    modifierIds.forEach((id) => delete next[id]);
                    return next;
                });
                setDefaultModifiers((prev) => {
                    const next = {...prev};
                    modifierIds.forEach((id) => delete next[id]);
                    return next;
                });
            }

            newSubTypes[index] = {
                subType: value,
                multiSelection: newSubTypes[index].multiSelection,
            };
            return newSubTypes;
        });
    };

    const getModifiersByType = (type: string): DropdownOption[] => {
        const uniqueSubTypes = new Set(
            allItemModifiers
                .filter((modifier) => modifier.type === type)
                .map((modifier) => modifier.sub_type)
        );
        return Array.from(uniqueSubTypes).map((subType) => ({
            label: subType.charAt(0).toUpperCase() + subType.slice(1),
            value: subType,
        }));
    };

    const getModifiersBySubType = (subType: string): Modifier[] => {
        return allItemModifiers.filter(
            (modifier) => modifier.sub_type === subType
        );
    };

    const clearModifiers = () => {
        setSubTypes([{subType: "", multiSelection: false}]);
        setCheckedModifiers({});
        setDefaultModifiers({});
        setSelectedItemModifiers([]);
        setSelectedModifierType("");
    };

    // Drag-to-reorder handlers
    const handleDragStart = (e: React.DragEvent, index: number) => {
        setDragIndex(index);
        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData("text/plain", index.toString());
    };

    const handleDragOver = (e: React.DragEvent, index: number) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        if (dragIndex !== index) {
            setDragOverIndex(index);
        }
    };

    const handleDragLeave = () => {
        setDragOverIndex(null);
    };

    const handleDrop = (e: React.DragEvent, dropIndex: number) => {
        e.preventDefault();
        const fromIndex = dragIndex;
        if (fromIndex === null || fromIndex === dropIndex) {
            setDragIndex(null);
            setDragOverIndex(null);
            return;
        }

        const updated = [...subTypes];
        const [moved] = updated.splice(fromIndex, 1);
        updated.splice(dropIndex, 0, moved);
        setSubTypes(updated);

        setDragIndex(null);
        setDragOverIndex(null);
    };

    const handleDragEnd = () => {
        setDragIndex(null);
        setDragOverIndex(null);
    };

    // Validation: compute per-row errors
    const subTypeErrors = selectedModifierType
        ? subTypes.map((st) => {
              if (!st.subType) {
                  return "Please select a sub-type";
              }
              const hasCheckedOption = selectedItemModifiers.some(
                  (m) => m.sub_type === st.subType
              );
              if (!hasCheckedOption) {
                  return "Please select at least one option";
              }
              return "";
          })
        : [];

    const hasValidationErrors =
        selectedModifierType !== "" &&
        subTypeErrors.some((err) => err !== "");

    useEffect(() => {
        onValidationChange?.(!hasValidationErrors);
    }, [hasValidationErrors, onValidationChange]);

    // Build ordered selectedItemModifiers based on subTypes order for serialization
    const orderedModifiers = subTypes.flatMap((st) =>
        selectedItemModifiers.filter((m) => m.sub_type === st.subType)
    );

    return (
        <>
            <div className="col-span-6">
                <CustomSelect
                    title="Add Modifiers?"
                    name="modifier_type"
                    value={selectedModifierType}
                    onChange={(v) => {
                        setSelectedModifierType(v);
                        setSubTypes([{subType: "", multiSelection: false}]);
                        setCheckedModifiers({});
                        setDefaultModifiers({});
                        setSelectedItemModifiers([]);
                    }}
                    options={modifierTypes}
                />
            </div>
            {selectedModifierType &&
                subTypes.map((subType, index) => (
                    <div
                        key={index}
                        draggable
                        onDragStart={(e) => handleDragStart(e, index)}
                        onDragOver={(e) => handleDragOver(e, index)}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => handleDrop(e, index)}
                        onDragEnd={handleDragEnd}
                        className={`col-span-6 grid grid-cols-6 gap-5 bg-gray-100 p-2 rounded ${
                            dragIndex === index ? "opacity-50" : ""
                        } ${
                            dragOverIndex === index
                                ? "border-t-2 border-primary-500"
                                : ""
                        }`}
                    >
                        <div className="col-span-5 flex items-start gap-2">
                            <Bars3Icon
                                className="w-6 h-6 mt-8 text-gray-400 cursor-grab active:cursor-grabbing flex-shrink-0"
                            />
                            <div className="flex-1">
                                <CustomSelect
                                    title={`Sub-Type ${index + 1}`}
                                    name={`sub_type${index}`}
                                    onChange={(v) => updateSubType(index, v)}
                                    options={getModifiersByType(selectedModifierType)}
                                    value={subType.subType}
                                />
                            </div>
                        </div>
                        <div className="col-span-1">
                            <IndeterminateCheckbox
                                label="Multi Selection?"
                                name={`multi_selection${index}`}
                                onChange={(checked) =>
                                    handleMultiSelectionChange(
                                        index,
                                        checked as boolean
                                    )
                                }
                                defaultChecked={subType.multiSelection}
                            />
                        </div>
                        {getModifiersBySubType(subTypes[index].subType).map(
                            (modifier) => (
                                <div className="col-span-1" key={modifier.id}>
                                    <div
                                        className={`flex items-center gap-4 rounded p-1 ${
                                            modifier.additional_price < 0
                                                ? "bg-red-100 border border-red-300"
                                                : "bg-gray-300"
                                        }`}
                                    >
                                        <IndeterminateCheckbox
                                            label={`${modifier.name} (${usdFormat.format(
                                                modifier.additional_price / 100
                                            )})`}
                                            name={`modifier-${modifier.id}`}
                                            defaultChecked={
                                                checkedModifiers[modifier.id] || false
                                            }
                                            onChange={(isChecked) =>
                                                handleCheckboxChange(
                                                    modifier,
                                                    isChecked
                                                )
                                            }
                                        />
                                        {subTypes[index].multiSelection && (
                                            <IndeterminateCheckbox
                                                label="Default?"
                                                name={`default-${modifier.id}`}
                                                defaultChecked={
                                                    defaultModifiers[modifier.id] ||
                                                    false
                                                }
                                                onChange={(isChecked) =>
                                                    handleDefaultChange(
                                                        modifier.id,
                                                        isChecked as boolean
                                                    )
                                                }
                                            />
                                        )}
                                    </div>
                                </div>
                            )
                        )}
                        {subTypeErrors[index] && (
                            <div className="col-span-6">
                                <p className="text-sm text-red-600">
                                    {subTypeErrors[index]}
                                </p>
                            </div>
                        )}
                        {subTypes.length > 1 && (
                            <div className="col-span-6">
                                <PrimaryButton
                                    type="button"
                                    onClick={() => handleRemoveSubType(index)}
                                >
                                    - Remove Sub-Type
                                </PrimaryButton>
                            </div>
                        )}
                    </div>
                ))}
            {selectedModifierType && (
                <div className="sm:col-span-1 col-span-2">
                    <PrimaryButton type="button" onClick={handleAddSubType}>
                        + Add Modifier
                    </PrimaryButton>
                </div>
            )}
            {selectedModifierType && (
                <div className="sm:col-span-1 col-span-2">
                    <PrimaryButton type="button" onClick={clearModifiers}>
                        Clear Modifiers
                    </PrimaryButton>
                </div>
            )}
            <input
                type="hidden"
                value={JSON.stringify(orderedModifiers)}
                name="selected_item_modifiers"
            />
        </>
    );
}
